import type { Nullable } from "@core/domain/types/nullable.type.ts";
import type { StringKeyof } from "@core/domain/types/string-keyof.type.ts";
import * as z from "zod";

export enum OrderDirection {
    Asc = "ascend",
    Desc = "descend",
}

type OrderBy<T extends object> = StringKeyof<T>;

export interface OrderInputProps<T extends object> {
    field: OrderBy<T>;
    direction?: Nullable<OrderDirection>;
}

export interface OrderProps<T extends object> extends OrderInputProps<T> {
    direction: Nullable<OrderDirection>;
}

const orderBrandSchema = z.literal("Order").brand<"Order">();
export class Order<T extends object> {
    readonly __brand = orderBrandSchema;

    private readonly initialField: OrderProps<T>["field"];
    private readonly initialDirection: OrderProps<T>["direction"];

    private constructor(
        private readonly props: Readonly<OrderProps<T>>,
        initialField: OrderProps<T>["field"],
        initialDirection: OrderProps<T>["direction"],
    ) {
        this.initialField = initialField;
        this.initialDirection = initialDirection;
    }

    get field(): OrderProps<T>["field"] {
        return this.props.field;
    }

    get direction(): OrderProps<T>["direction"] {
        return this.props.direction;
    }

    static create<T extends object>(props: OrderInputProps<T>): Order<T> {
        return new Order<T>(
            { direction: OrderDirection.Desc, ...props },
            props.field,
            props.direction ?? null,
        );
    }

    update(props: Partial<OrderInputProps<T>>): Order<T> {
        return new Order<T>(
            { ...this.props, ...props },
            this.initialField,
            this.initialDirection,
        );
    }

    reset(): Order<T> {
        return new Order<T>(
            { field: this.initialField, direction: this.initialDirection },
            this.initialField,
            this.initialDirection,
        );
    }
}
